import {
  ChangeDetectorRef,
  Component,
  Input,
  viewChild,
  inject,
  input,
  output,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { take } from 'rxjs/operators';
import { AlarmLimit } from 'src/app/model/operational-monitoring/alarm-limit';
import { AlarmLimitsTableService } from 'src/app/services/table/alarm-limits-table.service';
import { AuthService } from 'src/app/services/auth.service';
import { DeviceService } from 'src/app/services/device.service';
import { Subscription, firstValueFrom, forkJoin } from 'rxjs';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { ConfirmationService, SelectItem } from '@openng/optimus-ui/api';
import { FacilityApiService } from 'src/app/services/api/facility-api.service';
import { LocationApiService } from 'src/app/services/api/location-api.service';
import { isNullOrUndefined, sortByProperty } from 'src/app/utils/utils';
import { OperationalMonitoringApiService } from 'src/app/services/api/operational-monitoring-api.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { Facility } from 'src/app/model/locations/facility';
import { AlarmLimitAuditTrailComponent } from '../../components/alarm-limit-audit-trail/alarm-limit-audit-trail.component';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { SelectChangeEvent, Select } from '@openng/optimus-ui/select';
import { Dialog } from '@openng/optimus-ui/dialog';
import { isValidDateTimeInput } from 'src/app/core/functions/date-functions';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { InputText } from '@openng/optimus-ui/inputtext';
import { DatePicker } from '@openng/optimus-ui/datepicker';
import { ButtonDirective } from '@openng/optimus-ui/button';

@Component({
  selector: 'app-alarm-limits-detail-container',
  templateUrl: './alarm-limits-detail-container.component.html',
  styleUrls: ['./alarm-limits-detail-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    Dialog,
    ProgressSpinnerComponent,
    FormsModule,
    ReactiveFormsModule,
    Select,
    InputText,
    DatePicker,
    ButtonDirective,
    AlarmLimitAuditTrailComponent,
    TranslatePipe,
  ],
})
export class AlarmLimitsDetailContainerComponent implements OnInit {
  protected _deviceService = inject(DeviceService);
  protected _auth = inject(AuthService);
  protected _tableService = inject(AlarmLimitsTableService);
  private _translateService = inject(TranslateService);
  private _authApi = inject(AuthApiService);
  private _facilityApi = inject(FacilityApiService);
  protected _locationApi = inject(LocationApiService);
  protected _opMonitoringApi = inject(OperationalMonitoringApiService);
  protected _logAndMessage = inject(LogAndMessageService);
  private _confirmationService = inject(ConfirmationService);

  @Input() isVisible: boolean = false;
  readonly formType = input<string>('Create');
  readonly selectedAlarmLimit = input<AlarmLimit>();

  readonly hideForm = output();
  readonly formSubmitted = output();
  readonly alarmLimitDeleted = output<string>();
  readonly formIsReady = output();

  readonly auditTrail = viewChild.required<AlarmLimitAuditTrailComponent>(
    'alarmLimitAuditTrail'
  );

  dialogHeader: string = '';
  refreshSubscription?: Subscription;
  availableActions: SelectItem[] = [];
  selectedAction: SelectItem | null = null;
  showSuccessMessage = true;

  deleteMsg: string = '';
  okBtnLabel: string = '';
  cancelBtnLabel: string = '';
  deleteHeader: string = '';

  canCreate: boolean = false;
  canEditAll: boolean = false;
  canEditOwn: boolean = false;
  canEditDirectReports: boolean = false;
  enableCanEdit: boolean = false;
  loading: boolean = true;
  saving: boolean = false;

  locations: any[] = [];
  facilities: Facility[] = [];
  facilityNames: string[] = [];
  limitedAccessFacilities: Facility[] = [];

  minDateTime = new Date();
  auditTrailVisible: boolean = false;

  datatypes: string[] = [];

  alarmLimitForm = new UntypedFormGroup({
    facility: new UntypedFormControl('', Validators.required),
    alarmAttributeName: new UntypedFormControl('', Validators.required),
    assetIdentifier: new UntypedFormControl('', Validators.required),
    limitValue: new UntypedFormControl('', Validators.required),
    effectiveDateTime: new UntypedFormControl(new Date(), Validators.required),
    updater: new UntypedFormControl({ value: '', disabled: true }),
    updatedAt: new UntypedFormControl({ value: '', disabled: true }),
    updateReason: new UntypedFormControl('', Validators.required),
    datatype: new UntypedFormControl(null, Validators.required),
  });

  ngOnInit() {
    this.canEditAll = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.FIELD_OPERATIONS.ALARM_LIMITS.CHANGE_ALL_ALARM_LIMITS,
    ]);
    this.canEditOwn = this._authApi.doesUserHaveAtLeastOneClaimFromList([
      CLAIMS.FIELD_OPERATIONS.ALARM_LIMITS.CHANGE_OWN_ALARM_LIMITS,
    ]);
    this.canEditDirectReports =
      this._authApi.doesUserHaveAtLeastOneClaimFromList([
        CLAIMS.FIELD_OPERATIONS.ALARM_LIMITS.CHANGE_DIRECT_REPORTS_ALARM_LIMITS,
      ]);
    this.prepareForm();
    this.setupLabels();
  }

  prepareForm() {
    this.loadFacilities();
    this.alarmLimitForm.disable();
    this.datatypes = ['Boolean', 'DateTime', 'Double', 'String'];
  }

  onDialogHide($event: any) {
    this.hideForm.emit();

    setTimeout(() => {
      this.alarmLimitForm.disable();
      this.alarmLimitForm.markAsPending();
      this.selectedAction = null;
      this.enableCanEdit = false;

      this.facilities = this.facilities.filter((fac) => {
        return this.limitedAccessFacilities.every(
          (limitedFac) => limitedFac.id !== fac.id
        );
      });

      this.limitedAccessFacilities = [];
      this.alarmLimitForm.patchValue({
        facility: null,
        assetIdentifier: null,
        alarmAttributeName: null,
        limitValue: null,
        effectiveDateTime: new Date(),
        updater: null,
        updatedAt: null,
        updateReason: null,
        datatype: null,
      });
    });
  }

  async fillActionsDropdown() {
    this.availableActions = [];
    const labels = await firstValueFrom(
      forkJoin({
        select: this._translateService.get('COMMON.LABEL.SELECT_ACTION'),
        audit: this._translateService.get('COMMON.LABEL.VIEW_AUDIT_INFO'),
        delete: this._translateService.get(
          'OPMONITORING.LABEL.DELETE_CONFIGURATION'
        ),
      })
    );

    // always show placeholder and audit trail,
    // because if they can make it to this page, they have perms
    this.availableActions.push({
      label: labels.select,
      value: null,
    });
    this.availableActions.push({
      label: labels.audit,
      value: 'view-audit-info',
    });

    if (this.canEditAll || this.enableCanEdit) {
      this.availableActions.push({
        label: labels.delete,
        value: 'delete-alarm-limit',
      });
      this.enableForm();
    }
  }

  loadFacilities() {
    if (this.canEditAll) {
      this._facilityApi
        .getFacilities()
        .pipe(take(1))
        .subscribe(
          (data) => {
            if (data.length === 0) {
              setTimeout(() => {
                this.loadFacilities();
              }, 1000);
              return;
            }
            this.facilities = data;
            this.facilities = this.facilities.filter(
              (facility) => facility.hidden == false
            );
            this.sortFacilitiesDropdown();
          },
          (error) => console.error(error)
        );
    } else {
      this._facilityApi
        .getUserFacilities(this._auth.getEmail()!, this.canEditDirectReports)
        .subscribe({
          next: (userFacilities) => {
            this.facilities = userFacilities;
            this.sortFacilitiesDropdown();
          },
          error: (error) => console.error(error),
        });
    }
  }

  sortFacilitiesDropdown() {
    this.facilities.sort((a, b) => sortByProperty(a, b, 'facilityName'));
    this.loading = false;
    this.formIsReady.emit();
  }

  onFacilityChanged($event: any) {
    this.alarmLimitForm.patchValue({
      facility: $event.value,
    });
  }

  updateFields() {
    const selectedAlarmLimit = this.selectedAlarmLimit()!;
    if ((this.canEditOwn || this.canEditDirectReports) && !this.canEditAll) {
      if (
        !isNullOrUndefined(
          this.facilities.find(
            (fac) => fac.id === selectedAlarmLimit.facilityId
          )
        )
      ) {
        this.enableCanEdit = true;
      } else {
        this.facilities.push(selectedAlarmLimit.Facility);
        this.limitedAccessFacilities.push(selectedAlarmLimit.Facility);
      }
    }

    this.alarmLimitForm.patchValue({
      facility: this.facilities.find(
        (fac) => fac.id === selectedAlarmLimit.facilityId
      ),
      assetIdentifier: selectedAlarmLimit.assetIdentifier,
      alarmAttributeName: selectedAlarmLimit.alarmAttributeName,
      limitValue: selectedAlarmLimit.value,
      updater: selectedAlarmLimit.updater,
      updatedAt: selectedAlarmLimit.updatedAt.toLocaleString(),
      effectiveDateTime: selectedAlarmLimit.effectiveDateTime,
      datatype: selectedAlarmLimit.datatype,
    });

    this.fillActionsDropdown();
  }

  onActionSelected($event: SelectChangeEvent) {
    let action = $event.value;
    if (action === 'delete-alarm-limit') {
      this.deleteAlarmLimit();
    }

    if (action === 'view-audit-info') {
      this.auditTrail().getHistories();
      this.auditTrailVisible = true;
    }
  }

  onSubmit() {
    if (!this.alarmLimitForm.valid) {
      console.log('Form was somehow submitted without being valid!');
      return;
    }

    if (!this.validateDatatypeAndValue()) {
      this._logAndMessage.translateToErrorAlert({
        bodyKey: 'OPMONITORING.MESSAGES.ERROR.DATATYPE_VALUE_MISMATCH',
        headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
      });
      return;
    }

    const formType = this.formType();
    if (formType === 'Create') {
      this.createAlarmLimit();
    }

    if (formType === 'Edit') {
      this.updateAlarmLimit();
    }
  }

  setupLabels(): void {
    const deleteMsg$ = this._translateService.get(
      'OPMONITORING.MESSAGES.CONFIRMATION.DELETE_ALARM_LIMIT'
    );
    const ok$ = this._translateService.get('COMMON.LABEL.BUTTONS.YES');
    const cancel$ = this._translateService.get('COMMON.LABEL.BUTTONS.NO');
    const header$ = this._translateService.get(
      'OPMONITORING.MESSAGES.HEADERS.DELETE_ALARM_LIMIT'
    );
    forkJoin([deleteMsg$, ok$, cancel$, header$]).subscribe((messages) => {
      this.deleteMsg = messages[0];
      this.okBtnLabel = messages[1];
      this.cancelBtnLabel = messages[2];
      this.deleteHeader = messages[3];
    });
  }

  createAlarmLimit() {
    this.saving = true;
    let addSubmittedAlarm = this.alarmLimitForm.value;

    let newAlarmLimit = {
      facilityId: addSubmittedAlarm.facility.id,
      assetIdentifier: addSubmittedAlarm.assetIdentifier,
      alarmAttributeName: addSubmittedAlarm.alarmAttributeName,
      value: addSubmittedAlarm.limitValue,
      effectiveDateTime: addSubmittedAlarm.effectiveDateTime,
      updatedAt: new Date(),
      updateReason: addSubmittedAlarm.updateReason,
      datatype: addSubmittedAlarm.datatype,
    };

    this._opMonitoringApi.createAlarmLimit(newAlarmLimit).subscribe({
      next: (alarmLimit) => {
        if (alarmLimit.id) {
          this.saving = true;
          this.isVisible = false;
          let createdAlarmLimit = alarmLimit;
          this.formSubmitted.emit({
            ...addSubmittedAlarm,
            id: createdAlarmLimit.id,
          });
        } else {
          console.warn('Succeeded without getting an id back?');
          this.isVisible = false;
        }
      },
      error: (error) => {
        console.error(error);
        this.saving = false;
        this._logAndMessage.errorLogOnly(error);
        this._logAndMessage.translateToErrorAlert({
          bodyKey: 'OPMONITORING.MESSAGES.ERROR.CREATE_ALARM_LIMITS',
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        });
      },
      complete: () => {
        this.saving = false;
        if (this.showSuccessMessage) {
          this.showSuccessMessage = false;
          this._logAndMessage.translateToSuccessMessage({
            bodyKey: 'OPMONITORING.MESSAGES.SUCCESS.CREATE_ALARM_LIMITS',
            headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
          });
        }
      },
    });
  }

  deleteAlarmLimit() {
    let deletingSchedule = this.selectedAlarmLimit()!;
    this._confirmationService.confirm({
      message: this.deleteMsg,
      accept: () => {
        this.saving = true;
        this._opMonitoringApi
          .deleteAlarmLimit(deletingSchedule.id ?? '')
          .subscribe({
            next: (result) => {
              this.isVisible = false;
              this.alarmLimitDeleted.emit(deletingSchedule.id);
            },
            error: (error) => {
              console.error(error);
              this.saving = false;
              this._logAndMessage.errorLogOnly(error);
              this._logAndMessage.translateToErrorAlert({
                bodyKey: 'OPMONITORING.MESSAGES.ERROR.DELETE_ALARM_LIMIT',
                headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
              });
            },
            complete: () => {
              this.saving = false;
              if (this.showSuccessMessage) {
                this.showSuccessMessage = false;
                this._logAndMessage.translateToSuccessMessage({
                  bodyKey: 'OPMONITORING.MESSAGES.SUCCESS.DELETE_ALARM_LIMIT',
                  headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
                });
              }
            },
          });
      },
    });
  }

  updateAlarmLimit() {
    this.saving = true;
    let editingAlarm = this.alarmLimitForm.value;

    let updatedAlarmLimit = {
      facilityId: editingAlarm.facility.id,
      assetIdentifier: editingAlarm.assetIdentifier,
      alarmAttributeName: editingAlarm.alarmAttributeName,
      value: editingAlarm.limitValue,
      effectiveDateTime: editingAlarm.effectiveDateTime,
      updatedAt: new Date(),
      updateReason: editingAlarm.updateReason,
      datatype: editingAlarm.datatype,
    };

    this._opMonitoringApi
      .updateAlarmLimit(this.selectedAlarmLimit()!.id, updatedAlarmLimit)
      .subscribe({
        next: (alarmLimit) => {
          if (alarmLimit.id) {
            this.isVisible = false;
            this.formSubmitted.emit({
              ...editingAlarm,
              id: this.selectedAlarmLimit()!.id,
            });
            this.saving = false;
          }
        },
        error: (error) => {
          console.error(error);
          this.saving = false;
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'OPMONITORING.MESSAGES.ERROR.EDIT_ALARM_LIMIT',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
        },
        complete: () => {
          this.saving = false;
          if (this.showSuccessMessage) {
            this.showSuccessMessage = false;
            this._logAndMessage.translateToSuccessMessage({
              bodyKey: 'OPMONITORING.MESSAGES.SUCCESS.EDIT_ALARM_LIMIT',
              headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
            });
          }
        },
      });
  }

  onAuditTrailHidden() {
    this.auditTrailVisible = false;
  }

  resetEffectiveDate() {
    this.alarmLimitForm.patchValue({
      effectiveDateTime: new Date(),
    });
  }

  enableForm() {
    this.alarmLimitForm.enable();
    this.alarmLimitForm.get('updater')?.disable();
    this.alarmLimitForm.get('updatedAt')?.disable();
  }

  validateDatatypeAndValue(): boolean {
    let selectedDatatype: string = this.alarmLimitForm.get('datatype')?.value;
    let valueInput: string = this.alarmLimitForm.get('limitValue')?.value;

    switch (selectedDatatype) {
      case 'String':
        return true; // Nothing to validate for strings
      case 'Double':
        const regex = /^-*\d+(.{1}\d+)*$/;
        return regex.test(valueInput);
      case 'DateTime':
        return isValidDateTimeInput(valueInput);
      case 'Boolean':
        return (
          valueInput.toLowerCase() === 'false' ||
          valueInput.toLowerCase() === 'true'
        );
    }
    return false;
  }
}
